import CheckableTag from 'antd/es/tag/CheckableTag'
import { DrilldownTableExtraColumn } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  DrilldownTablePanelData,
} from 'features/drilldown/components/table/DrilldownTable'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import {
  entryToIndication,
  entryToIndicationSet,
  RolodexIndicationContextProvider,
  ToggledIndicationDetailView,
} from 'features/indicationTriage'
import { Maximize } from 'lucide-react'
import { FC, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

type IndicationTablePanelData = DrilldownTablePanelData

const INDICATION_TABLE_COLUMN_DEFS: ReadonlyArray<DrilldownTableColumnDef> = [
  {
    title: 'Site',
    field: 'site.name',
  },
  {
    title: 'Asset',
    field: 'unit.name',
    linkTemplate: '/assets/{site.id}/{unit.id}',
  },
  {
    title: 'Component',
    field: 'component.id',
    linkTemplate: '/details/{component.id}',
  },
  {
    title: 'Indication',
    field: 'indication.id',
    linkTemplate:
      '/details/{component.id}?extension=IndicationTriage&indication={indication.id}',
  },
  {
    title: 'Classification',
    field: 'indication.classificationInfo.classification',
    tagColor: {
      enableTags: 'enableTags',
    },
  },
  {
    title: 'Damage Mechanism',
    field: 'indication.detail.damage_mechanism',
    tagColor: {
      enableTags: 'enableTags',
    },
  },
]

const IndicationDetailCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const [indicationEntry, ...indicationEntries] = useMemo(
    () => entries.filter((e) => e.type === 'indication'),
    [entries]
  )

  const indicationSetEntry = useMemo(
    () =>
      entries
        .filter((e) => e.type === RolodexEntryType.INDICATION_SET)
        .find(
          (e) =>
            indicationEntry &&
            e.id === indicationEntry.tags[RolodexEntryType.INDICATION_SET]
        ),
    [entries, indicationEntry]
  )

  const [isOpen, setOpen] = useState<boolean>(false)
  const [indicationSet, indication] = useMemo(
    () => [
      indicationSetEntry ? entryToIndicationSet(indicationSetEntry) : undefined,
      entryToIndication(indicationEntry),
    ],
    [indicationEntry, indicationSetEntry]
  )
  return (
    indication &&
    indicationSet &&
    !indicationEntries.length && (
      <>
        <RolodexIndicationContextProvider indicationId={indicationEntry.id}>
          <ToggledIndicationDetailView
            isOpen={isOpen}
            close={() => setOpen(false)}
            indicationItem={indication}
            cardConfig={indicationSet.detailCards}
            type='modal'
          />
        </RolodexIndicationContextProvider>
        <CheckableTag checked={isOpen} onChange={(checked) => setOpen(checked)}>
          <Maximize />
        </CheckableTag>
      </>
    )
  )
}

export const INDICATION_TABLE_EXTRA_COLS: ReadonlyArray<DrilldownTableExtraColumn> =
  [
    {
      id: 'col_indication_detail',
      name: 'Detail',
      linked_types: [
        RolodexEntryType.INDICATION,
        RolodexEntryType.INDICATION_SET,
      ],
      render: (props) => <IndicationDetailCol {...props} />,
    },
  ]

export const DEFAULT_INDICATION_TABLE_PANEL_DATA: IndicationTablePanelData = {
  ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  title: 'Indications',
  isPinned: true,
  columns: INDICATION_TABLE_COLUMN_DEFS,
  groupByType: RolodexEntryType.INDICATION,
  enabledExtraColumnIds: ['col_indication_detail'],
}
